import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../config/api";
import { generateQueryParams } from "../../utils/generateQueryParams";

// Fetch onboarded users
export const fetchOnboardedUsers = createAsyncThunk(
  "admin/fetch/onboardedUsers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryString = generateQueryParams(params);
      const url = `admin/users/pending${queryString ? `?${queryString}` : ""}`;
      const response = await api.get(url);
      console.log("Fetched users response:", response.data);

      // Handle direct array response
      return Array.isArray(response.data)
        ? response.data
        : response.data.data || [];
    } catch (err) {
      console.error("Error fetching users:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch users"
      );
    }
  }
);

export const fetchAllUsers = createAsyncThunk(
  "admin/fetch/allUsers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryString = generateQueryParams(params);
      const response = await api.get(
        `admin/users/all${queryString ? `?${queryString}` : ""}`
      );
      console.log("Fetched all users response:", response.data);
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch all users");
    }
  }
);

// Verify a user
export const verifyUser = createAsyncThunk(
  "admin/verify/user",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.patch(`admin/users/verify/${userId}`);
      console.log("User verified:", response.data);
      return {
        userId,
        user: response.data.user,
      };
    } catch (err) {
      console.error("Error verifying user:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to verify user"
      );
    }
  }
);

// Reject a user
export const rejectUser = createAsyncThunk(
  "admin/reject/user",
  async ({ userId, reason }, { rejectWithValue }) => {
    try {
      const response = await api.delete(`admin/users/${userId}`, {
        data: { reason },
      });
      console.log("User rejected:", response.data);
      return userId;
    } catch (err) {
      console.error("Error rejecting user:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to reject user"
      );
    }
  }
);

// Delete a verified user
export const deleteUser = createAsyncThunk(
  "admin/delete/user",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.delete(`admin/users/delete/${userId}`);
      console.log("User deleted:", response.data);
      return userId;
    } catch (err) {
      console.error("Error deleting user:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to delete user"
      );
    }
  }
);

// Fetch client profile by ID
export const fetchUserProfileById = createAsyncThunk(
  "admin/fetch/userProfileById",
  async ({ userId, ...filters }) => {
    const queryString = generateQueryParams(filters);
    const response = await api.get(
        `/admin/users/${userId}/profile${queryString ? `?${queryString}` : ""}`
      );
    return response.data;
  }
);

// NOTE: Get Client Profile
export const getClientProfile = createAsyncThunk("user/profile", async () => {
  const response = await api.get("/user/profile/");
  return response.data;
});

// NOTE: Edit Client Profile Details
export const editClientProfile = createAsyncThunk(
  "user/editProfile",
  async (payload) => {
    const response = await api.put("/user/profile", payload);
    return response.data;
  }
);

// ========== ADMIN / STAFF =========

// NOTE: Get Admin / Staff Profile
export const getProfile = createAsyncThunk("admin/profile", async () => {
  const response = await api.get("/admin/profile/");
  return response.data;
});

// NOTE: Edit Admin / Staff Profile Details
export const editProfile = createAsyncThunk(
  "admin/editProfile",
  async (payload) => {
    const response = await api.put("/admin/profile", payload);
    return response.data;
  }
);

// NOTE:Updating Currency
export const updateUserCurrency = createAsyncThunk(
  "admin/updateCurrency",
  async ({ userId, currency }) => {
    const response = await api.put(`/admin/${userId}/update-currency`, { currency });
    return response.data;
  }
);

// NOTE: Updating Manual Payment ability
export const updateManualPaymentStatus = createAsyncThunk(
  "admin/updateManualPaymentStatus",
  async ({ userId, enabledManualPayments }) => {
    const response = await api.put(`/admin/${userId}/manual-payments`, { 
      enabledManualPayments 
    });
    return response.data;
  }
);

// NOTE: Add quick message to user
export const addQuickMessage = createAsyncThunk(
  "user/addQuickMessage",
  async ({ userId, message }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/user/${userId}/quick-message`, {
        message: message.trim(),
      });
      return response.data;
    } catch (err) {
      console.error("Error adding quick message:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to add quick message"
      );
    }
  }
);

// NOTE: Remove quick message from user
export const removeQuickMessage = createAsyncThunk(
  "user/removeQuickMessage",
  async ({ userId, messageIndex }, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/user/${userId}/quick-message`, {
        data: {
          messageIndex,
        },
      });
      return response.data;
    } catch (err) {
      console.error("Error removing quick message:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to remove quick message"
      );
    }
  }
);

// Fetch My Tasks for Sub-Admin
export const fetchMyTasks = createAsyncThunk(
  "user/fetch/myTasks",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryString = generateQueryParams(params);
      const response = await api.get(
        `admin/my-tasks${queryString ? `?${queryString}` : ""}`
      );
      console.log("Fetched my tasks response:", response.data);
      return response.data.data;
    } catch (err) {
      console.error("Error fetching my tasks:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch my tasks"
      );
    }
  }
);